/**
 * use-livechat — chat engine behind the standalone Live Chat widget.
 *
 * Human-support only: every conversation opens a VChat room directly (no AI
 * bot, no [EN] language prefix, no escalation trigger). The first message in a
 * fresh conversation performs `request-human` (connect + open room); resuming
 * an existing conversation performs `join-room` (sync missed history).
 *
 * Self-contained (local React state + localStorage, no zustand) and adapted for
 * the authenticated dashboard. The UI stays 100% on the design system; this
 * hook only owns data + transport.
 *
 * Split layout:
 *   livechat-store.ts  — types, localStorage persistence, conversation factories
 *   livechat-socket.ts — ServerMessage type + protocol constants
 *   use-livechat.ts    — React hook: state + socket lifecycle
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { io, type Socket } from 'socket.io-client';
import { env } from '@/config/env';
import {
  uid,
  deriveTitle,
  makeConversation,
  loadStore,
  saveStore,
  initStore,
  identitySession,
  resolveName,
  storageKey,
  UUID_RE,
  DEFAULT_TITLE,
  type ChatMessage,
  type ChatRole,
  type Conversation,
  type ConversationSummary,
  type LiveChatStatus,
  type LiveChatUser,
  type StoreShape,
} from './livechat-store';
import { SOCKET_TIMEOUT_MS, HISTORY_LIMIT, type ServerMessage } from './livechat-socket';
import { uploadImage } from './livechat-upload';

export type {
  ChatRole,
  ChatMessage,
  Conversation,
  ConversationSummary,
  LiveChatStatus,
  LiveChatUser,
} from './livechat-store';

export interface UseLiveChat {
  conversations: ConversationSummary[];
  activeId: string;
  messages: ChatMessage[];
  typing: boolean;
  status: LiveChatStatus;
  agentName: string | null;
  /** Send a user message — opens or resumes the VChat room as needed. */
  send: (text: string) => void;
  /** Upload and send an image attachment. Resolves once the emit is dispatched. */
  sendImage: (file: File) => Promise<void>;
  /** Start a fresh support conversation and make it active. */
  newConversation: () => void;
  /** Switch the active conversation (resumes its room if needed). */
  selectConversation: (id: string) => void;
  /** Delete a conversation (keeps at least one). */
  deleteConversation: (id: string) => void;
}

/** Map a server message `type` to our attachment kind (undefined = text). */
function attachmentKind(type?: string): 'image' | 'file' | undefined {
  if (type === 'image') return 'image';
  if (type === 'file') return 'file';
  return undefined;
}

export function useLiveChat(user: LiveChatUser | null): UseLiveChat {
  const [store, setStore] = useState<StoreShape>(() => initStore(user));
  const [typing, setTyping] = useState(false);
  const [connectingId, setConnectingId] = useState<string | null>(null);

  const { conversations, activeId } = store;

  // Refs so async / socket callbacks read fresh values.
  const storeRef = useRef(store);
  storeRef.current = store;
  const userRef = useRef(user);
  userRef.current = user;

  // Session id tied to the signed-in user (stable across reloads).
  const sessionRef = useRef<string>('');
  if (!sessionRef.current) sessionRef.current = identitySession(user);

  const socketRef = useRef<Socket | null>(null);
  const connectPromiseRef = useRef<Promise<void> | null>(null);
  const joinedRef = useRef<Set<string>>(new Set());
  // Chat JWT from the VChat 'connected' handshake — needed to presign uploads.
  const chatTokenRef = useRef<string | null>(null);

  // Persist on every change (messages, rooms, active id).
  useEffect(() => {
    saveStore(userRef.current, store);
  }, [store]);

  // If the widget mounted before auth resolved, load the user's store later.
  const loadedKeyRef = useRef(storageKey(user));
  useEffect(() => {
    const key = storageKey(user);
    if (key === loadedKeyRef.current) return;
    loadedKeyRef.current = key;
    if (!UUID_RE.test(sessionRef.current)) {
      sessionRef.current = identitySession(user);
    }
    const loaded = loadStore(user);
    if (loaded) setStore(loaded);
  }, [user]);

  // --- state updates -------------------------------------------------------
  const patchConversation = useCallback(
    (id: string, fn: (c: Conversation) => Conversation) => {
      setStore((s) => ({
        ...s,
        conversations: s.conversations.map((c) => (c.id === id ? fn(c) : c)),
      }));
    },
    [],
  );

  const appendMessage = useCallback(
    (convId: string, partial: Omit<ChatMessage, 'id' | 'time'>) => {
      setStore((s) => ({
        ...s,
        conversations: s.conversations.map((c) => {
          if (c.id !== convId) return c;
          const m: ChatMessage = { id: uid(), time: Date.now(), ...partial };
          const title =
            c.title === DEFAULT_TITLE && partial.role === 'user'
              ? deriveTitle(partial.content)
              : c.title;
          return { ...c, messages: [...c.messages, m], title, updatedAt: m.time };
        }),
      }));
    },
    [],
  );

  const appendByRoom = useCallback(
    (roomId: string, partial: Omit<ChatMessage, 'id' | 'time'>) => {
      setStore((s) => ({
        ...s,
        conversations: s.conversations.map((c) => {
          if (c.roomId !== roomId) return c;
          const m: ChatMessage = { id: uid(), time: Date.now(), ...partial };
          return {
            ...c,
            messages: [...c.messages, m],
            agentName: partial.agentName ?? c.agentName,
            updatedAt: m.time,
          };
        }),
      }));
    },
    [],
  );

  const mergeRoomHistory = useCallback((convId: string, serverMsgs: ServerMessage[]) => {
    setStore((s) => ({
      ...s,
      conversations: s.conversations.map((c) => {
        if (c.id !== convId) return c;
        const seen = new Set(c.messages.map((m) => m.id));
        let lastAgent = c.agentName;
        const mapped: ChatMessage[] = [];
        for (const sm of serverMsgs) {
          if (seen.has(sm.id)) continue;
          const isAgent = sm.sender === 'agent' || sm.sender === 'employee';
          const role: ChatRole = isAgent ? 'agent' : sm.sender === 'user' ? 'user' : 'system';
          if (isAgent && sm.user?.name) lastAgent = sm.user.name;
          const kind = attachmentKind(sm.type);
          mapped.push({
            id: sm.id,
            role,
            content: sm.content,
            time: new Date(sm.createdAt).getTime() || Date.now(),
            agentName: isAgent ? sm.user?.name : undefined,
            kind,
            fileUrl: kind ? sm.fileUrl : undefined,
            fileName: kind ? sm.fileName : undefined,
            fileSize: kind ? sm.fileSize : undefined,
          });
        }
        if (!mapped.length) return c;
        return { ...c, messages: [...c.messages, ...mapped], agentName: lastAgent, updatedAt: Date.now() };
      }),
    }));
  }, []);

  // --- socket.io human support --------------------------------------------
  const setupHandlers = useCallback(
    (socket: Socket) => {
      socket.on('new-message', (data: unknown) => {
        const d = data as {
          content?: string;
          sender?: string;
          roomId?: string;
          user?: { name?: string };
          type?: string;
          fileUrl?: string;
          fileName?: string;
          fileSize?: number;
        };
        const kind = attachmentKind(d.type);
        if (!d.content && !d.fileUrl) return;
        const isAgent = d.sender === 'agent' || d.sender === 'employee';
        const partial = {
          role: (isAgent ? 'agent' : 'system') as ChatRole,
          content: d.content ?? '',
          agentName: isAgent ? d.user?.name : undefined,
          kind,
          fileUrl: kind ? d.fileUrl : undefined,
          fileName: kind ? d.fileName : undefined,
          fileSize: kind ? d.fileSize : undefined,
        };
        if (d.roomId) appendByRoom(d.roomId, partial);
        else appendMessage(storeRef.current.activeId, partial);
      });
      socket.on('typing', () => setTyping(true));
      socket.on('stop-typing', () => setTyping(false));
      socket.on('user-joined', (data: unknown) => {
        const d = data as { name?: string; user_type?: string };
        if (d?.user_type === 'employee' && d?.name) {
          const activeConvId = storeRef.current.activeId;
          patchConversation(activeConvId, (c) => ({ ...c, agentName: d.name! }));
          appendMessage(activeConvId, { role: 'system', content: `${d.name} joined the chat.` });
        }
      });
      socket.on('disconnect', () => {
        joinedRef.current.clear();
      });
    },
    [appendByRoom, appendMessage, patchConversation],
  );

  // Open the socket once per session (resolves on VChat 'connected').
  const ensureSocket = useCallback((): Promise<void> => {
    if (socketRef.current?.connected) return Promise.resolve();
    if (connectPromiseRef.current) return connectPromiseRef.current;

    if (!/^https?:\/\//i.test(env.chat.socketUrl)) {
      console.error(
        `[LiveChat] APP_CHAT_SOCKET_URL must be an absolute http(s) origin (got "${env.chat.socketUrl}"); ` +
          `APP_CHAT_SOCKET_PATH must be the engine.io path like "/event" (got "${env.chat.socketPath}").`,
      );
      return Promise.reject(new Error('socket misconfigured'));
    }
    const email = userRef.current?.email?.trim();
    if (!email) return Promise.reject(new Error('missing account email'));
    const name = resolveName(userRef.current);

    const p = new Promise<void>((resolve, reject) => {
      const socket = io(env.chat.socketUrl, {
        path: env.chat.socketPath,
        query: { session_id: sessionRef.current, email, name },
        // Default transports (polling → websocket upgrade) so customers behind
        // proxies that block raw WebSocket still connect via long-polling.
        reconnection: false,
        timeout: SOCKET_TIMEOUT_MS,
      });
      socketRef.current = socket;
      setupHandlers(socket);
      // Fail the attempt if VChat never authenticates within the window.
      const authTimer = setTimeout(() => reject(new Error('auth timeout')), SOCKET_TIMEOUT_MS);
      // VChat authenticates from the query params, then emits 'connected'
      // with a short-lived chat JWT used to presign file uploads.
      socket.on('connected', (data: unknown) => {
        chatTokenRef.current = (data as { chatToken?: string })?.chatToken ?? null;
        clearTimeout(authTimer);
        resolve();
      });
      socket.on('connect_error', (e: unknown) => {
        clearTimeout(authTimer);
        reject(e instanceof Error ? e : new Error('connect_error'));
      });
      // The server can accept the socket then immediately close it — e.g. it
      // refuses this identity as a customer visitor (staff/agent emails).
      // Treat a pre-auth close as failure so the UI stops showing "Connecting…".
      socket.on('disconnect', () => {
        clearTimeout(authTimer);
        reject(new Error('closed before auth'));
      });
    });
    connectPromiseRef.current = p;
    p.catch(() => {
      connectPromiseRef.current = null;
      socketRef.current?.disconnect();
      socketRef.current = null;
    });
    return p;
  }, [setupHandlers]);

  // Open a NEW human-support room for a conversation.
  const requestRoom = useCallback(
    (convId: string): Promise<void> =>
      new Promise<void>((resolve, reject) => {
        const socket = socketRef.current;
        if (!socket) return reject(new Error('no socket'));
        const conv = storeRef.current.conversations.find((c) => c.id === convId);
        const history = (conv?.messages ?? []).slice(-HISTORY_LIMIT).map((m) => ({
          content: m.content,
          sender: m.role === 'user' ? 'user' : 'agent',
        }));
        // `platform: 'console'` tags the room as originating from the Partner
        // Console so agents can tell it apart from the website live chat.
        socket.emit('request-human', { roomId: convId, chatHistory: history, platform: 'console' }, (response: unknown) => {
          const r = (Array.isArray(response) ? response[0] : response) as {
            success?: boolean;
            room_id?: string;
            message?: string;
          };
          if (r?.success) {
            const roomId = r.room_id || convId;
            joinedRef.current.add(roomId);
            patchConversation(convId, (c) => ({ ...c, roomId }));
            appendMessage(convId, {
              role: 'system',
              content: 'Connected — an agent will be with you shortly.',
            });
            resolve();
          } else {
            appendMessage(convId, {
              role: 'system',
              content: r?.message || 'No agent is available right now. Please try again.',
            });
            reject(new Error('request-human failed'));
          }
        });
      }),
    [appendMessage, patchConversation],
  );

  // Re-join an EXISTING room and sync any missed messages.
  const joinRoom = useCallback(
    (convId: string): Promise<void> =>
      new Promise<void>((resolve) => {
        const socket = socketRef.current;
        const conv = storeRef.current.conversations.find((c) => c.id === convId);
        if (!socket || !conv?.roomId) return resolve();
        const msgs = conv.messages;
        const history = msgs.slice(-HISTORY_LIMIT).map((m) => ({
          content: m.content,
          sender: m.role === 'user' ? 'user' : 'agent',
        }));
        const lastMessageTimestamp = msgs.length ? Math.max(...msgs.map((m) => m.time)) : undefined;
        socket.emit(
          'join-room',
          { roomId: conv.roomId, chatHistory: history, lastMessageTimestamp },
          (response: unknown) => {
            const res = response as { success?: boolean; messages?: ServerMessage[] };
            if (res?.success) {
              joinedRef.current.add(conv.roomId!);
              if (res.messages?.length) mergeRoomHistory(convId, res.messages);
            }
            resolve();
          },
        );
      }),
    [mergeRoomHistory],
  );

  // Connect + open a room for a fresh conversation.
  const startRoom = useCallback(
    async (convId: string) => {
      const conv = storeRef.current.conversations.find((c) => c.id === convId);
      if (!conv || conv.roomId) return;
      setConnectingId(convId);
      appendMessage(convId, { role: 'system', content: 'Connecting you to a support agent…' });
      try {
        await ensureSocket();
        await requestRoom(convId);
      } catch {
        appendMessage(convId, { role: 'system', content: 'Could not connect. Please try again.' });
      } finally {
        setConnectingId(null);
      }
    },
    [ensureSocket, requestRoom, appendMessage],
  );

  // Resume a conversation's live connection (connect + join if needed).
  const resume = useCallback(
    async (convId: string) => {
      const conv = storeRef.current.conversations.find((c) => c.id === convId);
      if (!conv?.roomId) return;
      if (socketRef.current?.connected && joinedRef.current.has(conv.roomId)) return;
      setConnectingId(convId);
      try {
        await ensureSocket();
        await joinRoom(convId);
      } catch {
        // user can retry by sending
      } finally {
        setConnectingId(null);
      }
    },
    [ensureSocket, joinRoom],
  );

  // --- public API ----------------------------------------------------------
  const send = useCallback(
    (raw: string) => {
      const text = raw.trim();
      if (!text) return;
      const convId = storeRef.current.activeId;
      const conv = storeRef.current.conversations.find((c) => c.id === convId);
      if (!conv) return;

      appendMessage(convId, { role: 'user', content: text });

      const deliver = () => {
        const room = storeRef.current.conversations.find((c) => c.id === convId)?.roomId;
        if (socketRef.current?.connected && room) {
          socketRef.current.emit('send-message', { roomId: room, content: text });
        }
      };

      if (conv.roomId && socketRef.current?.connected && joinedRef.current.has(conv.roomId)) {
        deliver();
      } else if (conv.roomId) {
        void resume(convId).then(deliver);
      } else {
        void startRoom(convId).then(deliver);
      }
    },
    [appendMessage, resume, startRoom],
  );

  // Upload an image, append it locally, then deliver it to the agent. Needs a
  // live socket first (the chat JWT from 'connected' authorizes the upload).
  const sendImage = useCallback(
    async (file: File) => {
      const convId = storeRef.current.activeId;
      if (!storeRef.current.conversations.some((c) => c.id === convId)) return;

      try {
        await ensureSocket();
      } catch {
        appendMessage(convId, { role: 'system', content: 'Could not connect. Please try again.' });
        return;
      }

      let uploaded;
      try {
        uploaded = await uploadImage(file, chatTokenRef.current);
      } catch {
        appendMessage(convId, {
          role: 'system',
          content: 'Could not send the image. Please try a smaller image and try again.',
        });
        return;
      }

      const { url, name, size } = uploaded;
      appendMessage(convId, {
        role: 'user',
        content: name,
        kind: 'image',
        fileUrl: url,
        fileName: name,
        fileSize: size,
      });

      const deliver = () => {
        const room = storeRef.current.conversations.find((c) => c.id === convId)?.roomId;
        if (socketRef.current?.connected && room) {
          socketRef.current.emit('send-message', {
            roomId: room,
            content: name,
            metadata: { fileUrl: url, fileName: name, fileSize: size, type: 'image' },
          });
        }
      };

      const conv = storeRef.current.conversations.find((c) => c.id === convId);
      if (conv?.roomId && joinedRef.current.has(conv.roomId)) deliver();
      else if (conv?.roomId) await resume(convId).then(deliver);
      else await startRoom(convId).then(deliver);
    },
    [appendMessage, ensureSocket, resume, startRoom],
  );

  const newConversation = useCallback(() => {
    const c = makeConversation();
    setStore((s) => ({ conversations: [c, ...s.conversations], activeId: c.id }));
    setTyping(false);
  }, []);

  const selectConversation = useCallback(
    (id: string) => {
      setStore((s) => (s.activeId === id ? s : { ...s, activeId: id }));
      setTyping(false);
      void resume(id);
    },
    [resume],
  );

  const deleteConversation = useCallback((id: string) => {
    setStore((s) => {
      const remaining = s.conversations.filter((c) => c.id !== id);
      if (remaining.length === 0) {
        const c = makeConversation();
        return { conversations: [c], activeId: c.id };
      }
      const nextActive = s.activeId === id ? remaining[0].id : s.activeId;
      return { conversations: remaining, activeId: nextActive };
    });
  }, []);

  // Resume the active conversation's room once on mount (receive while closed).
  const didResumeRef = useRef(false);
  useEffect(() => {
    if (didResumeRef.current) return;
    didResumeRef.current = true;
    const s = storeRef.current;
    const active = s.conversations.find((c) => c.id === s.activeId);
    if (active?.roomId) void resume(active.id);
  }, [resume]);

  // Disconnect the socket when the widget unmounts.
  useEffect(
    () => () => {
      socketRef.current?.disconnect();
      socketRef.current = null;
    },
    [],
  );

  const active = conversations.find((c) => c.id === activeId) ?? conversations[0];
  const status: LiveChatStatus =
    connectingId === activeId ? 'connecting' : active?.roomId ? 'live' : 'idle';

  return {
    conversations: conversations.map((c) => ({
      id: c.id,
      title: c.title,
      connected: !!c.roomId,
      updatedAt: c.updatedAt,
    })),
    activeId,
    messages: active?.messages ?? [],
    typing,
    status,
    agentName: active?.agentName ?? null,
    send,
    sendImage,
    newConversation,
    selectConversation,
    deleteConversation,
  };
}
